import { extensionMenu } from '@lib/extensions';
import { createSidebar } from '@lib/plugins/sidebar';
import type { useAppContextStore } from '@lib/plugins/layouts/context';

import type { SidebarItemType } from '@lib/stores/app-sidebar-menu';
import type { AuthUser } from '@lib/types';

/**
 * The app has no context, so an entry is guarded by a plain global permission through
 * the item's own `permissions` field, and one without it is open to every user - the lib checks it with auth.check(). See
 * docs/decisions.md on why the team context was dropped.
 *
 * Dividers and headers are placed unconditionally: the lib drops the ones whose section ends
 * up empty, so no section has to know whether the one above it is there. See lib/docs/sidebar.md.
 */

function buildSidebarMenu(user: AuthUser, context: ReturnType<typeof useAppContextStore>): Array<SidebarItemType> {
	return [
		{ to: '/dashboard', icon: 'fa-home', label: 'dashboard' },

		// Above the two domain sections on purpose: both are read across the whole office
		// rather than belonging to onboarding or to case work.
		{ to: '/tasks', icon: 'fa-list-check', label: 'tasks' },

		{ to: '/calendar', icon: 'fa-calendar-days', label: 'calendar' },

		// The shared notebook, next to the two entries above for the same reason: it belongs
		// to the office rather than to onboarding or to case work. The notes of a Kanzlei and
		// of a Fall stay where the record is, as a tab - see docs/screens.md.
		{ to: '/notes', icon: 'fa-book', label: 'notes' },

		{ divider: true },
		{ label: 'crm_header' },
		{ to: '/pipeline', icon: 'fa-diagram-subtask', label: 'pipeline', permissions: ['law_firms.read'] },
		{ to: '/law-firms', icon: 'fa-scale-balanced', label: 'law_firms', permissions: ['law_firms.read'] },

		{ divider: true },
		{ label: 'work_header' },
		{ to: '/matters', icon: 'fa-folder-open', label: 'matters', permissions: ['matters.read'] },

		{ divider: true },
		{ label: 'admin_header' },
		{ to: '/admin/users', icon: 'fa-users', label: 'users', permissions: ['users.read'] },
		// The Standorte and their letterhead addresses. Own permission, because a Standort is
		// maintained by whoever runs the offices, not by whoever configures the app.
		{ to: '/admin/locations', icon: 'fa-building', label: 'locations', permissions: ['locations.write'] },
		// The Schriftverkehr-Vorlagen the correspondence tab drafts from.
		{
			to: '/admin/correspondence-templates',
			icon: 'fa-envelope-open-text',
			label: 'correspondence_templates',
			permissions: ['correspondence_templates.write'],
		},
		// The tags a Kanzlei is marked with and the research import sets.
		{ to: '/admin/tags', icon: 'fa-tags', label: 'tags', permissions: ['tags.write'] },
		{ to: '/admin/settings', icon: 'fa-cogs', label: 'settings', permissions: ['app.settings'] },
		{
			to: '/admin/maintenance',
			icon: 'fa-screwdriver-wrench',
			label: 'maintenance',
			permissions: ['app.developer'],
		},
		// The API docs, signed in with the SPA session; the opener carries the token across.
		{ href: '/docs/swagger', icon: 'fa-book', label: 'swagger', opener: true, permissions: ['app.developer'] },
		// The extensions' admin pages, today the MCP server's info page beside Swagger UI
		...extensionMenu('admin', user, context),
	];
}

export default createSidebar({ menu: buildSidebarMenu, badges: 'me/sidebar_badges' });
